var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { RouterStatefullHandler, } from "@opendash/router";
import { Navigate, createBrowserRouter, } from "@opendash/router/dist/internal";
import { makeAutoObservable, runInAction } from "mobx";
import React from "react";
import { RouterErrorHandler, RouterLayoutBasic, RouterLayoutBasicPadding, RouterRootHandler, } from "..";
export class RouterService {
    // public params: ParamsType = {};
    constructor(framework) {
        this.__router = null;
        this.history = [];
        this.pathname = window.location.pathname;
        this.search = window.location.search;
        this.url = this.pathname + this.search;
        this.searchParams = getSearchParams(this.search);
        makeAutoObservable(this);
        this.framework = framework;
    }
    getURL() {
        return new URL(window.location.href);
    }
    navigate(url, options) {
        var _a;
        if (url instanceof URL) {
            url = url.pathname + url.search;
        }
        if ((options === null || options === void 0 ? void 0 : options.backIfPossible) && this.history.length > 0) {
            if (url === ((_a = this.history.at(-1)) === null || _a === void 0 ? void 0 : _a.url)) {
                return void this.back();
            }
        }
        if (options === null || options === void 0 ? void 0 : options.keepQueryParams) {
            url += this.search;
        }
        if (options === null || options === void 0 ? void 0 : options.replace) {
            this.replaceState(url);
        }
        else {
            this.pushState(url);
        }
    }
    isBackAvailable() {
        return this.history.length > 0;
    }
    back() {
        if (this.isBackAvailable()) {
            this.history.pop();
            this.go(-1);
        }
    }
    go(delta) {
        var _a;
        (_a = this.__router) === null || _a === void 0 ? void 0 : _a.navigate(delta);
    }
    pushState(url) {
        var _a;
        (_a = this.__router) === null || _a === void 0 ? void 0 : _a.navigate(url);
    }
    replaceState(url) {
        var _a;
        (_a = this.__router) === null || _a === void 0 ? void 0 : _a.navigate(url, { replace: true });
    }
    setSearchParam(key, value, options) {
        const params = new URLSearchParams(this.search);
        if (value) {
            params.set(key, value);
        }
        else {
            params.delete(key);
        }
        const search = params.toString();
        if (search) {
            this.navigate(this.pathname + "?" + search, options);
        }
        else {
            this.navigate(this.pathname, options);
        }
    }
    __getRoutes() {
        if (this.__router) {
            return this.__router;
        }
        const routes = this.framework._internal.routes;
        this.__router = createBrowserRouter([
            {
                path: "/",
                element: React.createElement(RouterRootHandler),
                errorElement: React.createElement(RouterErrorHandler),
                children: [
                    {
                        errorElement: React.createElement(RouterErrorHandler),
                        children: routes.map((route) => {
                            const path = route.path;
                            let element = null;
                            const RouteLayout = getLayout(route.layout);
                            if ("redirectPath" in route) {
                                element = React.createElement(Navigate, {
                                    to: route.redirectPath,
                                    replace: true,
                                });
                            }
                            else if ("state" in route && "componentSync" in route) {
                                element = React.createElement(RouterStatefullHandler, {
                                    key: path,
                                    RouteState: route.state,
                                    RouteComponent: route.componentSync,
                                    RouteLayout,
                                });
                            }
                            else if ("component" in route) {
                                element = React.createElement(React.lazy(route.component), route.props || {});
                            }
                            else if ("componentSync" in route) {
                                element = React.createElement(route.componentSync, route.props || {});
                            }
                            const loader = ({ request, params, }) => __awaiter(this, void 0, void 0, function* () {
                                if (!element) {
                                    throw new Response("", {
                                        status: 404,
                                        statusText: "Element Not Found",
                                    });
                                }
                                const authenticated = !!this.framework.services.UserService.isLoggedIn();
                                if (route.auth === "authenticated" && !authenticated) {
                                    throw new Response("", {
                                        status: 401,
                                        statusText: "Unauthorized",
                                    });
                                }
                                if (route.auth === "unauthenticated" && authenticated) {
                                    return new Response("", {
                                        status: 302,
                                        headers: {
                                            Location: "/",
                                        },
                                    });
                                }
                                const hasPermission = !route.permission ||
                                    (Array.isArray(route.permission)
                                        ? route.permission.every((p) => this.framework.services.UserService.hasPermission(p))
                                        : this.framework.services.UserService.hasPermission(route.permission));
                                if (!hasPermission) {
                                    throw new Response("", {
                                        status: 403,
                                        statusText: "Permission denied.",
                                    });
                                }
                                return null;
                            });
                            const childRoute = {
                                path,
                                element,
                                loader,
                            };
                            return childRoute;
                        }),
                    },
                ],
            },
        ]);
        this.__router.subscribe((state) => {
            // Params:
            // console.log(state.matches.at(-1)?.params);
            const pathname = state.location.pathname;
            const search = state.location.search;
            const url = pathname + search;
            if (this.url === url)
                return;
            runInAction(() => {
                this.history.push({
                    url: this.url,
                    pathname: this.pathname,
                    search: this.search,
                });
                this.url = url;
                this.pathname = pathname;
                this.search = search;
                this.searchParams = getSearchParams(search);
            });
        });
        return this.__router;
    }
}
function getSearchParams(search) {
    return Object.fromEntries(new URLSearchParams(search).entries());
}
function getLayout(layout) {
    if (!layout) {
        return null;
    }
    if (typeof layout === "string") {
        if (layout === "basic") {
            return RouterLayoutBasic;
        }
        if (layout === "basic-padding") {
            return RouterLayoutBasicPadding;
        }
        console.warn(`[$framework.router] Layout '${layout}' not found. Fallback to no layout.`);
        return null;
    }
    return layout;
}
//# sourceMappingURL=RouterService.js.map